import { faEdit, faPlus, faTrashAlt, faUndoAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import AdvancedSettingsService from '../../service/AdvancedSettingsService'
import { formatBooleanColumn } from '../../utils/DatatableUtil'
import { getDataField } from '../../utils/DialogUtil'
import GatewayType from '../GatewayType'

const AdvancedSettings: GatewayType = () => {
  const [advancedSettings, setAdvancedSettings] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    AdvancedSettingsService.get()
      .then((advancedSettings: any[]) => setAdvancedSettings(advancedSettings))
      .finally(() => setLoading(false))
  }, [])

  const columns = [
    {
      text: t('name.text'),
      dataField: 'label',
      sort: true,
      classes: 'position-relative',
      formatter: (cellData: any, row: any) => {
        return AuthService.hasPermission(userPermissions, Authorization.settings.advancedSettings.edit) ? (
          <ProtectedButton
            variant='link'
            className='text-decoration-none pl-0 stretched-link text-left'
            tooltip={t('edit')}
            onClick={() => editAdvancedSettingDialog(t, editDialogPropsSetter, setAdvancedSettings, row)}>
            <FontAwesomeIcon icon={faEdit} /> {cellData}
          </ProtectedButton>
        ) : (
          cellData
        )
      },
    },
    {
      text: t('groupName.text'),
      dataField: 'groupname',
      sort: true,
    },
    {
      text: t('value'),
      dataField: 'value',
      sort: true,
      formatter: (cell: any, row: any) => (row.type === 'Boolean' ? formatBooleanColumn(cell) : cell),
    },
    {
      hidden: !AuthService.hasPermission(userPermissions, Authorization.settings.advancedSettings.edit),
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      formatter: (cellData: any, row: any) => {
        return (
          <ProtectedButton
            permissions={Authorization.settings.advancedSettings.edit}
            variant='link'
            className='text-danger text-link text-decoration-none'
            tooltip={t('delete')}
            onClick={() => deleteAdvancedSettingDialog(t, confirmDialogPropsSetter, setAdvancedSettings, row)}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </ProtectedButton>
        )
      },
    },
  ]

  const data: any[] = advancedSettings
    .map((group) => {
      group.properties.forEach((property: any) => (property.groupname = group.name))
      return group.properties.filter((property: any) => property.default === false)
    })
    .flat()

  var captionButtons: CaptionButton[] = [
    {
      onClick: () => addAdvancedSettingsDialog(t, editDialogPropsSetter, setAdvancedSettings, advancedSettings),
      text: t('configure'),
      icon: faPlus,
      permissions: Authorization.settings.advancedSettings.edit,
    },
  ]

  if (data.length > 0) {
    captionButtons.push({
      onClick: () => restoreDefaultDialog(t, editDialogPropsSetter, setAdvancedSettings, advancedSettings),
      text: t('restoreDefaults'),
      icon: faUndoAlt,
      permissions: Authorization.settings.advancedSettings.edit,
    })
  }

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='name'
        data={data}
        columns={columns}
        caption={t('advancedSettings.text')}
        helpLink={t('advancedSettings.helpLink')}
        captionButtons={captionButtons}
      />
    </Loading>
  )
}

function restoreDefaultDialog(t: TFunction, editDialogPropsSetter: any, setAdvancedSettings: React.Dispatch<any>, advancedSettings: any[]) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('advancedSettings.restoreDefaultsHeader'),
    onSubmit: AdvancedSettingsService.retoreDefaults,
    onSubmitExtraArgs: [t, setAdvancedSettings],
    data: [
      {
        id: 'groupName',
        type: 'select',
        options: advancedSettings.map((group) => group.name),
        value: advancedSettings[0].name,
        label: t('groupName.text'),
        required: true,
      },
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function addAdvancedSettingsDialog(t: TFunction, editDialogPropsSetter: any, setAdvancedSettings: React.Dispatch<any>, advancedSettings: any[]) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('advancedSettings.configureHeader'),
    onSubmit: AdvancedSettingsService.updateSetting,
    onSubmitExtraArgs: [t, setAdvancedSettings],
    data: (custom: string[]) => [
      {
        id: 'groupName',
        type: 'select',
        options: advancedSettings.map((group) => group.name),
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value, custom[1]]),
        value: custom[0],
        label: t('groupName.text'),
        required: true,
      },
      {
        id: 'name',
        type: 'typeAhead',
        options: advancedSettings
          .find((group: any) => group.name === custom[0])
          .properties.filter((setting: any) => setting.default === true)
          .map((setting: any) => ({ label: setting.label, value: setting.name })),
        onChange: (e: any, setCustom: React.Dispatch<any>) => {
          setCustom([custom[0], e.length ? (e[0].value ? e[0].value : e[0]) : ''])
        },
        value: [custom[1]],
        label: t('name.text'),
        required: true,
      },
      ...getValueField(advancedSettings, custom),
    ],
    initCustom: [advancedSettings[0].name, ''],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function editAdvancedSettingDialog(t: TFunction, editDialogPropsSetter: any, setAdvancedSettings: React.Dispatch<any>, advancedSetting: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('advancedSettings.editHeader'),
    onSubmit: AdvancedSettingsService.updateSetting,
    onSubmitExtraArgs: [t, setAdvancedSettings],
    data: [
      {
        id: 'groupName',
        type: 'text',
        value: advancedSetting.groupname,
        label: t('groupName.text'),
        plaintext: true,
        readOnly: true,
      },
      {
        id: 'name',
        type: 'text',
        value: advancedSetting.name,
        label: t('groupName.text'),
        plaintext: true,
        readOnly: true,
      },
      ...[getDataField(advancedSetting)],
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteAdvancedSettingDialog(t: TFunction, confirmDialogPropsSetter: any, setAdvancedSettings: any, row: any) {
  const data = {
    groupName: row.groupname,
    name: row.name,
  }
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('advancedSettings.deleteHeader'),
    body: t('advancedSettings.deleteBody'),
    submitText: t('yes'),
    onSubmit: AdvancedSettingsService.updateSetting,
    onSubmitExtraArgs: [data, t, setAdvancedSettings],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function getValueField(advancedSettings: any[], custom: string[]) {
  return advancedSettings
    .find((group: any) => group.name === custom[0])
    .properties.filter((setting: any) => setting.name === custom[1])
    .map((setting: any) => getDataField(setting, setting.name, 1))
}

export default AdvancedSettings
